/**
 * Act 2 — T1 trace arc. Contract capture → doc carousel → reconciliation
 * → invoice FSM → logistics → compliance → single-window clearance.
 */

import type { ReactElement } from "react";
import type { Act2Screen } from "@/lib/scenes/screens";
import type { SceneRenderContext } from "@/lib/scenes/act4";
import { ContractCaptureWithHandshake } from "@/components/scene/ContractCaptureWithHandshake";
import { DocumentCarousel }     from "@/components/scenes/act2/DocumentCarousel";
import { ReconciliationMatrix } from "@/components/scenes/act2/ReconciliationMatrix";
import { InvoiceFsmTile }       from "@/components/scenes/act2/InvoiceFsmTile";
import { SmartLogistics }       from "@/components/scenes/act2/SmartLogistics";
import { ComplianceSnapshot }   from "@/components/scenes/act2/ComplianceSnapshot";
import { SingleWindowReveal }   from "@/components/scenes/act2/SingleWindowReveal";
import { EscrowFundedHandshake } from "@/components/scenes/act2/EscrowFundedHandshake";
import { CraneIllustration }    from "@/components/scenes/shared/illustrations/CraneIllustration";
import { pickInvoiceState }     from "@/components/scene/SceneRouterHelpers";
import { USER_ACTION }          from "@/lib/userActions/UserActionsContext";

export const ACT2_RENDERERS: Record<Act2Screen, (ctx: SceneRenderContext) => ReactElement> = {
  "2.1": ({ ev, signParty }) => (
    <ContractCaptureWithHandshake
      ev={ev}
      signParty={signParty}
      requireSignCta
      signCtaActionKey={USER_ACTION.T1_SIGN_CONTRACT}
    />
  ),
  "2.2": ({ ev, graph }) => (
    <DocumentCarousel documents={ev.documents} graph={graph} />
  ),
  "2.3": ({ ev }) => <ReconciliationMatrix reconciliation={ev.reconciliation} />,
  "2.4": ({ ev, signParty, invoiceSignerHeader }) => (
    <InvoiceFsmTile
      invoiceState={pickInvoiceState(ev, "INV-2026-0042")}
      einvoiceSigner={signParty.einvoice}
      einvoicePdf={signParty.einvoicePdf}
      invoiceSignerHeader={invoiceSignerHeader}
      requireSignCta
      ctaActionKey={USER_ACTION.T1_SIGN_INVOICE}
    />
  ),
  "2.4b": () => <EscrowFundedHandshake />,
  "2.5": ({ ev }) => {
    // Mirror T2 4.14 / T3 5.9 — drop the crane illustration in the
    // right column, lifting once the Loading milestone has fired so
    // the audience can spot the cold-chain handover beat visually.
    const loaded = ev.logisticsMilestones.some((m) => m.milestone === "Loading");
    return (
      <SmartLogistics
        milestones={ev.logisticsMilestones}
        rightColumnExtra={<CraneIllustration loaded={loaded} />}
      />
    );
  },
  "2.6": ({ ev }) => (
    <ComplianceSnapshot
      snapshot={ev.complianceSnapshot}
      expectedCorridorLabel={"Corridor Portal Único Siscomex — Dubai Mirsal Portal\nBrazil (origin) → UAE (destination)"}
      expectedAuthorities={["Receita Federal (BRL)", "E-invoicing and VAT (UAE)", "Dubai Trade", "MOCCAE Porto", "OFAC + UAE Sanctions (Internal Use Only)", "Compliance Hard Stops (Internal Use Only)", "TravelRule (Internal Use Only)"]}
    />
  ),
  "2.7": ({ ev }) => <SingleWindowReveal customs={ev.customsCleared} />,
};
